import { useRef, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Camera, Image as ImageIcon, X, Trash2, Navigation, CheckCircle2, Clock, XCircle, Loader2, MapPin, CircleParking, Info, CloudUpload } from 'lucide-react';
import { isNetworkError } from '@/lib/offlineQueue';
import { Stamp } from '@/components/passport/PassportGrid';
import { toast } from 'sonner';

const STATUS = {
  verified: { label: 'Acreditado', cls: 'bg-emerald-100 text-emerald-800 border-emerald-300', Icon: CheckCircle2 },
  pending: { label: 'En revisión', cls: 'bg-amber-100 text-amber-800 border-amber-300', Icon: Clock },
  rejected: { label: 'Rechazada', cls: 'bg-red-100 text-red-800 border-red-300', Icon: XCircle },
  queued: { label: 'Guardada en el móvil · pendiente de subir', cls: 'bg-blue-100 text-blue-900 border-blue-300', Icon: CloudUpload },
};

export default function WaypointSheet({ wp, registrationId, stampUrl, onClose, onChanged, onQueue, onUnqueue, online = true }) {
  const [showDetails, setShowDetails] = useState(false);
  const isInfo = wp.waypoint_type === 'info';
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const st = wp.status ? STATUS[wp.status] : null;
  const canUpload = !isInfo && (!wp.status || wp.status === 'rejected');

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    if (!navigator.onLine && onQueue) {
      await onQueue(wp, file);
      toast.info(`Sin cobertura: foto de ${wp.name} guardada en el móvil. Se subirá sola al recuperar señal.`);
      setBusy(false);
      return;
    }
    try {
      const fd = new FormData();
      fd.append('photo', file);
      fd.append('waypoint_id', wp.id);
      const r = await axios.post(`${API}/registrations/${registrationId}/tracking-photos`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const p = r.data.photo;
      if (p.status === 'verified') toast.success(`¡${wp.name} acreditado! ${p.validation_note || ''}`);
      else toast.info(p.validation_note || 'Foto enviada a revisión del Rally Master');
      await onChanged();
    } catch (e) {
      if (isNetworkError(e) && onQueue) {
        await onQueue(wp, file);
        toast.info(`Sin conexión: foto de ${wp.name} guardada en el móvil. Se subirá sola al recuperar señal.`);
      } else {
        toast.error(e.response?.data?.detail || 'No se pudo subir la foto');
      }
    } finally {
      setBusy(false);
      if (cameraRef.current) cameraRef.current.value = '';
      if (galleryRef.current) galleryRef.current.value = '';
    }
  };

  const removePhoto = async () => {
    if (!wp.photo || !window.confirm('¿Eliminar esta foto y volver a intentarlo?')) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/registrations/${registrationId}/tracking-photos/${wp.photo.id}`);
      toast.success('Foto eliminada');
      await onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo eliminar la foto');
    } finally { setBusy(false); }
  };

  return (
    <div className="absolute inset-x-0 bottom-0 z-[1100] md:inset-x-auto md:right-3 md:bottom-3 md:w-[380px]" data-testid={`wp-sheet-${wp.id}`}>
      <div className="bg-white rounded-t-2xl md:rounded-2xl shadow-2xl border border-gray-200 max-h-[70vh] overflow-y-auto">
        <div className="flex items-start gap-3 p-4 pb-2">
          {isInfo ? <CircleParking className="w-8 h-8 text-gray-600 shrink-0" /> : wp.status === 'verified'
            ? <Stamp wp={wp} stampUrl={stampUrl} size={44} className="shrink-0" />
            : <span className="mt-1 w-5 h-5 rounded-full shrink-0 border-2 border-white shadow" style={{ backgroundColor: wp.color || '#9ca3af' }} />}
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-bold text-gray-900 leading-tight" data-testid="wp-sheet-name">{wp.name}</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {isInfo ? 'Aparcamiento / punto informativo' : wp.category}{wp.code ? ` · ${wp.code}` : ''}{wp.points ? ` · ${wp.points} pts` : ''}{wp.province ? ` · ${wp.province}` : ''}{wp.period ? ` · ${wp.period}` : ''}
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-2 -m-2 text-gray-500 hover:text-gray-900" aria-label="Cerrar" data-testid="wp-sheet-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-4 space-y-3 pb-4">
          {wp.description && <p className="text-sm text-gray-700">{wp.description}</p>}

          {wp.details && Object.keys(wp.details).length > 0 && (
            <div className="rounded-lg border border-gray-200 bg-gray-50" data-testid="wp-sheet-details">
              <button type="button" onClick={() => setShowDetails((v) => !v)} className="w-full h-10 px-3 flex items-center justify-between text-sm font-semibold text-gray-800" data-testid="wp-sheet-details-toggle">
                <span className="flex items-center gap-1"><Info className="w-4 h-4 text-violet-600" /> Ficha del waypoint</span>
                <span className="text-gray-400 text-xs">{showDetails ? 'ocultar' : 'ver'}</span>
              </button>
              {showDetails && (
                <dl className="px-3 pb-3 space-y-1.5 text-xs">
                  {Object.entries(wp.details).map(([k, v]) => (
                    <div key={k}><dt className={`font-semibold ${k === 'Riesgo' ? 'text-amber-700' : 'text-gray-700'}`}>{k}</dt><dd className="text-gray-600">{v}</dd></div>
                  ))}
                </dl>
              )}
            </div>
          )}

          {wp.status === 'queued' && (
            <div className={`rounded-lg border p-3 flex items-center gap-3 ${st.cls}`} data-testid="wp-sheet-status">
              <CloudUpload className="w-5 h-5 shrink-0" />
              <div className="text-sm flex-1"><p className="font-semibold">{st.label}</p><p className="text-xs opacity-80">{online ? 'Se subirá en unos segundos' : 'Se subirá al recuperar cobertura'}</p></div>
              <button type="button" onClick={() => onUnqueue?.(wp.queued.id)} className="text-xs underline" data-testid="wp-unqueue-btn">Quitar</button>
            </div>
          )}
          {st && wp.status !== 'queued' && (
            <div className={`rounded-lg border p-3 flex gap-3 ${st.cls}`} data-testid="wp-sheet-status">
              {wp.photo?.photo_url && (
                <a href={wp.photo.photo_url} target="_blank" rel="noreferrer" className="shrink-0">
                  <img src={wp.photo.photo_url} alt="" className="w-16 h-16 rounded-md object-cover border border-white" />
                </a>
              )}
              <div className="text-sm min-w-0">
                <p className="font-semibold flex items-center gap-1"><st.Icon className="w-4 h-4" /> {st.label}</p>
                {wp.photo?.exif_distance_m != null && <p className="text-xs">A {Math.round(wp.photo.exif_distance_m)} m del waypoint (GPS de la foto)</p>}
                {wp.photo?.validation_note && <p className="text-xs opacity-80">{wp.photo.validation_note}</p>}
                {wp.photo?.rejection_reason && <p className="text-xs font-medium">Motivo: {wp.photo.rejection_reason}</p>}
              </div>
            </div>
          )}

          {canUpload && (
            <div className="grid grid-cols-2 gap-2">
              <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="wp-camera-input" />
              <input ref={galleryRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="wp-gallery-input" />
              <Button type="button" disabled={busy} onClick={() => cameraRef.current?.click()} className="h-12 bg-violet-600 hover:bg-violet-700 text-white" data-testid="wp-camera-btn">
                {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Camera className="w-5 h-5 mr-2" /> Hacer foto</>}
              </Button>
              <Button type="button" disabled={busy} onClick={() => galleryRef.current?.click()} variant="outline" className="h-12 border-violet-300 text-violet-800" data-testid="wp-gallery-btn">
                <ImageIcon className="w-5 h-5 mr-2" /> Galería
              </Button>
              <p className="col-span-2 text-[11px] text-gray-500 flex items-start gap-1"><MapPin className="w-3.5 h-3.5 shrink-0 mt-0.5" /> Se valida sola si el GPS de la foto está a menos de 500 m. Desde galería se conserva mejor la ubicación.</p>
            </div>
          )}

          <div className="flex gap-2">
            <a
              href={`https://www.google.com/maps/dir/?api=1&destination=${wp.latitude},${wp.longitude}`}
              target="_blank" rel="noreferrer"
              className="flex-1 h-11 inline-flex items-center justify-center rounded-md border border-gray-300 text-sm font-medium text-gray-800 hover:bg-gray-50"
              data-testid="wp-navigate-link"
            >
              <Navigation className="w-4 h-4 mr-2" /> Cómo llegar
            </a>
            {wp.photo && wp.status !== 'verified' && (
              <Button type="button" variant="outline" disabled={busy} onClick={removePhoto} className="h-11 border-red-300 text-red-700" data-testid="wp-delete-photo-btn">
                <Trash2 className="w-4 h-4 mr-1" /> Quitar foto
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
